/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace degenerate
*/
var degenerate = {};

/**
* @name cdf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/cdf}
*/
import cdf from './../../../../base/dists/degenerate/cdf';
setReadOnly( degenerate, 'cdf', cdf );

/**
* @name Degenerate
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/ctor}
*/
import Degenerate from './../../../../base/dists/degenerate/ctor';
setReadOnly( degenerate, 'Degenerate', Degenerate );

/**
* @name entropy
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/entropy}
*/
import entropy from './../../../../base/dists/degenerate/entropy';
setReadOnly( degenerate, 'entropy', entropy );

/**
* @name logcdf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/logcdf}
*/
import logcdf from './../../../../base/dists/degenerate/logcdf';
setReadOnly( degenerate, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/logpdf}
*/
import logpdf from './../../../../base/dists/degenerate/logpdf';
setReadOnly( degenerate, 'logpdf', logpdf );

/**
* @name logpmf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/logpmf}
*/
import logpmf from './../../../../base/dists/degenerate/logpmf';
setReadOnly( degenerate, 'logpmf', logpmf );

/**
* @name mean
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/mean}
*/
import mean from './../../../../base/dists/degenerate/mean';
setReadOnly( degenerate, 'mean', mean );

/**
* @name median
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/median}
*/
import median from './../../../../base/dists/degenerate/median';
setReadOnly( degenerate, 'median', median );

/**
* @name mgf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/mgf}
*/
import mgf from './../../../../base/dists/degenerate/mgf';
setReadOnly( degenerate, 'mgf', mgf );

/**
* @name mode
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/mode}
*/
import mode from './../../../../base/dists/degenerate/mode';
setReadOnly( degenerate, 'mode', mode );

/**
* @name pdf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/pdf}
*/
import pdf from './../../../../base/dists/degenerate/pdf';
setReadOnly( degenerate, 'pdf', pdf );

/**
* @name pmf
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/pmf}
*/
import pmf from './../../../../base/dists/degenerate/pmf';
setReadOnly( degenerate, 'pmf', pmf );

/**
* @name quantile
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/quantile}
*/
import quantile from './../../../../base/dists/degenerate/quantile';
setReadOnly( degenerate, 'quantile', quantile );

/**
* @name stdev
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/stdev}
*/
import stdev from './../../../../base/dists/degenerate/stdev';
setReadOnly( degenerate, 'stdev', stdev );

/**
* @name variance
* @memberof degenerate
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/degenerate/variance}
*/
import variance from './../../../../base/dists/degenerate/variance';
setReadOnly( degenerate, 'variance', variance );


// EXPORTS //

export default degenerate;
